import ModelEntity from "../model-entity"
import ModelProxy from "../model-proxy"
import {EntityType} from "../../gd/const/item"
import ErrorProto from "../../gd/validation/error"
import ErrorFieldRequired from "../../gd/validation/error-field-required"
import ApiResource from "../../gd/api/resources"
import {ResourceType, ResourceWorkSchedule} from "./@const"


export default class Resource extends ModelEntity {

    // id                  : ID
    companyId           : ID
    type                : string
    name                : string
    icon                : string | null
    color               : number | null
    notes               : string | null
    skills              : string[]
    workSchedule        : ResourceWorkSchedule | null
    financeInternalCost : number | null
    financeExternalCost : number | null
    isDeleted           : boolean
    momentCreated       : string

    constructor(data: any, useProxy: boolean = true) {
        super(data?.id, EntityType.RESOURCE)

        this.id                  = data['id']
        this.companyId           = data['companyId']
        this.type                = _.get(data, 'type', ResourceType.DEFAULT)
        this.name                = data['name']
        this.icon                = _.get(data, 'icon', null)
        this.color               = _.get(data, 'color', null)
        this.notes               = _.get(data, 'notes', null)
        this.skills              = _.get(data, 'skills') || []
        this.workSchedule        = _.get(data, 'workSchedule', null)
        this.financeInternalCost = _.get(data, 'financeInternalCost', null)
        this.financeExternalCost = _.get(data, 'financeExternalCost', null)
        this.isDeleted           = _.get(data, 'isDeleted', false)
        this.momentCreated       = data['momentCreated']

        // Proxy tracks property writes into `updatedFields` so save() can
        // PATCH only what actually changed. Constructor assignments above
        // bypass the proxy because `this` is still the raw instance.
        return useProxy ? new Proxy(this, ModelProxy) : this
    }

    get isPeople(): boolean {
        return this.type === ResourceType.PEOPLE
    }

    get isAssets(): boolean {
        return this.type === ResourceType.ASSETS
    }

    get isGeneric(): boolean {
        return this.type === ResourceType.GENERIC
    }

    clone(): Resource {
        return new Resource(this.toJSON())
    }

    validate(): ErrorProto[] | null {
        const errors: ErrorProto[] = []

        if (!this.name) errors.push(new ErrorFieldRequired(gd.fields.resource.name))
        if (!ResourceType.ALLOWED.includes(this.type)) {
            errors.push(new ErrorFieldRequired(gd.fields.resource.type))
        }

        return _.isEmpty(errors) ? null : errors
    }

    // Override to discard updatedFields entries that the delta itself
    // triggered through the proxy — those values came FROM the server,
    // so the next save() must not echo them back.
    applyDelta(data: any): boolean {
        const result = super.applyDelta(data)
        this.updatedFields.clear()
        return result
    }

    async save(): Promise<boolean> {
        const payload: any = {}

        this.updatedFields.forEach((fieldName: string) => {
            payload[fieldName] = (this as any)[fieldName]
        })

        this.updatedFields.clear()

        if (_.isEmpty(payload)) return false

        // Backend rejects non-empty skills when type !== 'people'.
        if (this.type !== ResourceType.PEOPLE && 'skills' in payload) {
            payload.skills = []
        }

        try {
            await ApiResource.update(this.id, payload).promise()
            return true
        } catch (e) {
            console.error('Resource save failed', e)
            gd.apiError(e)
            return false
        }
    }

    toJSON(): JSONObject {
        return {
            id                  : this.id,
            companyId           : this.companyId,
            type                : this.type,
            name                : this.name,
            icon                : this.icon,
            color               : this.color,
            notes               : this.notes,
            skills              : this.skills,
            workSchedule        : this.workSchedule,
            financeInternalCost : this.financeInternalCost,
            financeExternalCost : this.financeExternalCost,
            isDeleted           : this.isDeleted,
            momentCreated       : this.momentCreated,
        }
    }
}
